/**
 * Iter-411m — Radius Recommendation Card
 * =======================================
 *
 * Small embedded panel used inside AdminCreateShift / CreateShift that:
 *   1. Auto-recommends a GPS geofence radius (meters) based on venue
 *      signals (name/address/city/state/lat/lng + historical spread).
 *   2. Warns admins when their explicit override looks too tight for
 *      the venue type.
 *
 * Renders as a compact form field with an "Apply recommended" button.
 * The parent owns the `value` (in meters) and the `onChange(v)` callback.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Info, MapPin } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import api from '../utils/api';

export default function RadiusRecommendationCard({
  value,          // current radius (meters) or null/undefined for auto
  onChange,       // (newValue: number|null) => void
  venue_name,
  venue_address,
  city,
  state,
  venue_lat,
  venue_lng,
}) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRec = useCallback(async () => {
    // Wait for at least one meaningful venue signal before firing.
    if (!venue_name && !venue_address && venue_lat == null) {
      setRec(null);
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/admin/radius/recommend', {
        venue_name: venue_name || null,
        venue_address: venue_address || null,
        city: city || null,
        state: state || null,
        venue_lat: venue_lat ?? null,
        venue_lng: venue_lng ?? null,
        current_radius_m: value ? parseInt(value, 10) : null,
      });
      setRec(res.data);
    } catch (e) {
      // Silent — the panel simply doesn't render a recommendation.
      setRec(null);
    } finally {
      setLoading(false);
    }
  }, [venue_name, venue_address, city, state, venue_lat, venue_lng, value]);

  // Debounce venue changes so we don't spam the endpoint on every keypress.
  useEffect(() => {
    const t = setTimeout(fetchRec, 400);
    return () => clearTimeout(t);
  }, [fetchRec]);

  const numericVal = value == null || value === '' ? null : parseInt(value, 10);
  const recommended = rec?.recommended_m;
  const tooTight =
    recommended != null &&
    numericVal != null &&
    numericVal < Math.round(recommended * 0.6);

  return (
    <div
      className="space-y-2 md:col-span-2 p-3 border rounded-lg bg-slate-50"
      data-testid="radius-recommendation-card"
    >
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-slate-500" />
        <Label className="mb-0">
          GPS Geofence Radius (meters){' '}
          <span className="text-xs text-gray-400">
            (talent must be within this range to clock in/out — fallback paths always remain available)
          </span>
        </Label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="number"
          min={100}
          max={2000}
          placeholder={recommended ? `Auto: ${recommended}` : 'Auto (recommended)'}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
          className="w-40"
          data-testid="radius-input"
        />
        {recommended != null && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onChange(recommended)}
            data-testid="radius-apply-recommended"
          >
            Use recommended ({recommended} m)
          </Button>
        )}
        {value != null && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange(null)}
            data-testid="radius-clear"
          >
            Clear (auto)
          </Button>
        )}
      </div>
      {loading && (
        <p className="text-xs text-slate-500">Analyzing venue…</p>
      )}
      {rec && !loading && (
        <div className="flex items-start gap-2 text-sm">
          <Info className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
          <div className="text-slate-700">
            <span className="font-medium">Recommended: {recommended} m</span>{' '}
            <span className="text-slate-500">({rec.confidence} confidence)</span>
            <div className="text-xs text-slate-600 mt-0.5">{rec.reason}</div>
          </div>
        </div>
      )}
      {tooTight && (
        <div
          className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-sm"
          data-testid="radius-too-tight-alert"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
          <div className="text-amber-800">
            <span className="font-semibold">Radius may be too tight.</span>{' '}
            Talent at {venue_name || 'this venue'} could be unable to clock in/out. Consider using{' '}
            <button
              type="button"
              className="underline font-medium"
              onClick={() => onChange(recommended)}
            >
              {recommended} m
            </button>
            .
          </div>
        </div>
      )}
    </div>
  );
}
